import { useState, useEffect, useCallback } from "react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ArrowUpRight, ArrowDownRight, ChevronDown, BarChart3, RefreshCw } from "lucide-react";
import TradeChart from "@/components/TradeChart";
import api from "@/lib/api";
import { fmtDateTime, fmtPnl, fmtPrice, pnlCls, toUnix } from "./tradeFormat";

const useIsPhone = () => {
  const [isPhone, setIsPhone] = useState(() => typeof window !== "undefined" && window.innerWidth < 640);
  useEffect(() => {
    const onResize = () => setIsPhone(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isPhone;
};

const fmtPoints = (v) => (typeof v === "number" ? `${v > 0 ? "+" : ""}${v.toFixed(2)}` : "—");

function Stat({ label, value, cls = "text-[hsl(var(--ops-text))]" }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--ops-text-muted))]">{label}</div>
      <div className={`text-sm font-mono-num font-semibold truncate ${cls}`}>{value}</div>
    </div>
  );
}

function Reason({ title, text, note }) {
  return (
    <Card className="p-3 bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))]">
      <div className="text-xs font-semibold text-[hsl(var(--ops-text))] mb-1">{title}</div>
      {text
        ? <p className="text-[13px] leading-relaxed text-[hsl(var(--ops-text))]">{text}</p>
        : <p className="text-[13px] leading-relaxed text-[hsl(var(--ops-text-muted))]">{note || "Not known."}</p>}
      {text && note && <p className="mt-1.5 text-[11px] text-[hsl(var(--ops-text-muted))]">{note}</p>}
    </Card>
  );
}

const show = (v) => {
  if (v == null || v === "") return null;
  if (typeof v === "object") {
    const parts = Object.entries(v).filter(([, x]) => x != null && x !== "")
      .map(([k, x]) => `${k}: ${typeof x === "object" ? JSON.stringify(x) : x}`);
    return parts.length ? parts.join(" · ") : null;
  }
  return String(v);
};

function RawNotes({ d }) {
  const [open, setOpen] = useState(false);
  const e = d.entry_reason || {};
  const rows = [
    ["Pattern code", d.setup?.pattern || e.pattern],
    ["Setup level", d.setup?.level ?? e.level],
    ["Setup state", e.setup_state],
    ["Setup reason", e.setup_reason],
    ["Entry gate", e.entry_gate_reason != null ? `${e.entry_gate_allowed ? "allowed" : "blocked"}: ${e.entry_gate_reason}` : null],
    ["Smaller-chart check", e.lower_timeframe_reason != null ? `${e.lower_timeframe_confirmed ? "confirmed" : "not confirmed"}: ${e.lower_timeframe_reason}` : null],
    ["Stop check", e.technical_stop],
    ["Chart trends", e.timeframe_trends],
    ["Confidence", e.confidence],
    ["Decision time (UTC)", e.updated_at],
    ["Exit code", d.exit?.reason],
    ["Bot's close record", d.exit?.bot_record],
    ["Bot log at exit", d.exit?.retired_log],
    ["Stop/target from", d.levels?.source],
    ["Entry order", d.trade?.setup],
  ].map(([k, v]) => [k, show(v)]).filter(([, v]) => v);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card className="bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))]">
        <CollapsibleTrigger asChild>
          <button data-testid="trade-detail-raw-toggle"
            className="w-full flex items-center justify-between px-3 py-2.5 text-xs font-semibold text-[hsl(var(--ops-text))] hover:bg-[hsl(var(--ops-surface-2))] rounded-lg transition-colors duration-150">
            Bot's raw notes
            <ChevronDown className={`size-3.5 text-[hsl(var(--ops-text-muted))] transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-3 pb-3 space-y-2">
            {rows.length === 0 ? (
              <p className="text-xs text-[hsl(var(--ops-text-muted))]">The bot left no notes for this trade on the server.</p>
            ) : rows.map(([k, v]) => (
              <div key={k} className="text-xs">
                <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--ops-text-muted))]">{k}</div>
                <div className="text-[hsl(var(--ops-text))] [overflow-wrap:anywhere]">{v}</div>
              </div>
            ))}
          </div>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

export default function TradeDetail({ trade, onClose }) {
  const isPhone = useIsPhone();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const tradeId = trade?.trade_id;

  const load = useCallback(async () => {
    if (!tradeId) return;
    setData(null);
    setError(null);
    try {
      const { data } = await api.get(`/ops/trades/${encodeURIComponent(tradeId)}/detail`);
      setData(data);
    } catch (e) {
      const s = e?.response?.status;
      setError(s === 401 || s === 403 ? "Your sign-in has expired. Please sign in again."
        : s === 404 ? "This trade was not found." : "Could not load this trade. Check the connection and try again.");
    }
  }, [tradeId]);

  useEffect(() => { load(); }, [load]);

  const t = data?.trade || trade || {};
  const long = t.direction === "LONG";
  const lv = data?.levels || {};
  const n = data?.numbers || {};
  const notes = data?.notes || {};

  return (
    <Sheet open={!!trade} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" data-testid="trade-detail-sheet"
        className="w-full sm:max-w-2xl p-0 overflow-y-auto max-sm:overflow-x-hidden bg-[hsl(var(--ops-bg))] border-[hsl(var(--ops-border))] text-[hsl(var(--ops-text))]">
        <div className="p-4 sm:p-5 space-y-3">
          <div className="pr-8">
            <SheetTitle className="text-base font-display font-semibold text-[hsl(var(--ops-text))] flex items-center gap-2 flex-wrap">
              {t.instrument}
              <Badge className={`rounded text-[10px] border-0 px-1 gap-0.5 ${long ? "bg-[hsl(var(--ops-long))]/15 text-[hsl(var(--ops-long))]" : "bg-[hsl(var(--ops-short))]/15 text-[hsl(var(--ops-short))]"}`}>
                {long ? <ArrowUpRight className="size-2.5" /> : <ArrowDownRight className="size-2.5" />}{t.direction}
              </Badge>
              {t.pnl != null && <span className={`font-mono-num ${pnlCls(t.pnl)}`}>{fmtPnl(t.pnl)}</span>}
            </SheetTitle>
            <SheetDescription className="text-xs text-[hsl(var(--ops-text-muted))]">
              {fmtDateTime(t.entry_time, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </SheetDescription>
          </div>

          {error ? (
            <Card className="p-4 bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))] text-center space-y-2">
              <p className="text-sm text-[hsl(var(--ops-text-muted))]">{error}</p>
              <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={load}><RefreshCw className="size-3" /> Try again</Button>
            </Card>
          ) : !data ? (
            <div className="space-y-3">
              <Skeleton className="bg-[hsl(var(--ops-surface-2))]" style={{ height: isPhone ? 260 : 320 }} />
              <Skeleton className="h-20 bg-[hsl(var(--ops-surface-2))]" />
              <Skeleton className="h-16 bg-[hsl(var(--ops-surface-2))]" />
            </div>
          ) : (
            <>
              <Card className="bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))] overflow-hidden">
                {data.candles?.length ? (
                  <TradeChart candles={data.candles} direction={t.direction}
                    entryUnix={toUnix(t.entry_time)} exitUnix={toUnix(t.exit_time)}
                    entryPrice={t.entry_price} exitPrice={t.exit_price} stop={lv.stop} target={lv.target}
                    height={isPhone ? 260 : 320} />
                ) : (
                  <div className="flex flex-col items-center justify-center text-[hsl(var(--ops-text-muted))] px-4 text-center" style={{ height: 160 }}>
                    <BarChart3 className="size-6 mb-2 text-[hsl(var(--ops-brand))]" />
                    <span className="text-xs">{notes.candles || "No price bars for this trade."}</span>
                  </div>
                )}
                {data.candles?.length > 0 && (
                  <div className="px-3 py-1.5 border-t border-[hsl(var(--ops-border))] text-[10px] text-[hsl(var(--ops-text-muted))]">
                    One-minute bars, times in UTC{notes.candles ? ` · ${notes.candles}` : ""}
                  </div>
                )}
              </Card>

              <Card className="p-3 bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))]">
                <div data-testid="trade-detail-numbers" className="grid grid-cols-3 sm:grid-cols-5 gap-x-3 gap-y-2.5">
                  <Stat label="Direction" value={long ? "Long (buy)" : t.direction === "SHORT" ? "Short (sell)" : t.direction || "—"} cls={long ? "ops-long" : "ops-short"} />
                  <Stat label="Size" value={t.quantity != null ? `${t.quantity} contract${t.quantity === 1 ? "" : "s"}` : "—"} />
                  <Stat label="Entry" value={fmtPrice(t.entry_price)} />
                  <Stat label="Exit" value={fmtPrice(t.exit_price)} />
                  <Stat label="Time held" value={n.held_text || "—"} />
                  <Stat label="Stop" value={fmtPrice(lv.stop)} cls="ops-short" />
                  <Stat label="Target" value={fmtPrice(lv.target)} cls="text-[hsl(var(--ops-brand))]" />
                  <Stat label="Points" value={fmtPoints(n.points)} cls={pnlCls(n.points)} />
                  <Stat label="Result" value={t.pnl != null ? fmtPnl(t.pnl) : "—"} cls={pnlCls(t.pnl)} />
                </div>
                {notes.levels && <p className="mt-2 text-[11px] text-[hsl(var(--ops-text-muted))]">{notes.levels}</p>}
              </Card>

              <Reason title="Why it entered" text={data.explanation?.entry} note={notes.entry || (!data.explanation?.entry ? notes.setup : null)} />
              <Reason title="Why it exited" text={data.explanation?.exit} note={notes.exit} />
              <RawNotes d={data} />
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
